import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Inbox, AlertCircle, Flag, Trophy, CalendarDays, ArrowRight, ClipboardList,
  ShieldCheck, ShieldAlert, Archive, Activity, CheckCircle2, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtData } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } };

const fmtQuando = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

/**
 * Dashboard, diversa per ruolo.
 *
 * Amministratore e Responsabile vedevano la stessa identica pagina. Ma sono due
 * mestieri diversi: il Responsabile manda avanti l'oratorio e ha bisogno di
 * sapere cosa manca OGGI; l'Amministratore risponde del trattamento dei dati e
 * ha una domanda sola — «c'è qualcosa che non va?» — a cui nessuna schermata
 * rispondeva.
 *
 * Animatori e aiuti hanno una pagina propria (HomeAnimatore) e non passano di qui.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [classifica, setClassifica] = useState([]);
  const [cruscotto, setCruscotto] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/classifica").then((r) => setClassifica(r.data.classifica.slice(0, 3))).catch(() => {});
    if (admin) api.get("/cruscotto").then((r) => setCruscotto(r.data)).catch(() => setCruscotto(null));
  }, [admin]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  if (!data) return <p className="text-slate-400 font-semibold" data-testid="dashboard-loading">Caricamento...</p>;

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">
        Ciao, {user.nome}! 👋
      </h1>
      <p className="text-sm text-slate-500 font-semibold mb-6">
        Stagione {data.stagione} · iscrizioni pubbliche:{" "}
        {data.iscrizioni_aperte ? <span className="badge-pos">aperte</span> : <span className="badge-neg">chiuse</span>}
      </p>

      {admin && <Cruscotto dati={cruscotto} />}

      <DaFare dati={data} />

      {admin && (
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mt-8 mb-3">
          Quadro operativo
        </p>
      )}
      <Operativo dati={data} classifica={classifica} />
    </div>
  );
}


/* ==========================================================================
   AMMINISTRATORE
   ========================================================================== */

const STILE_GRAVITA = {
  alta: { box: "bg-red-50 border-red-200", testo: "text-red-800", punto: "bg-red-500" },
  media: { box: "bg-amber-50 border-amber-200", testo: "text-amber-900", punto: "bg-amber-500" },
  bassa: { box: "bg-slate-50 border-slate-200", testo: "text-slate-700", punto: "bg-slate-400" },
};

/**
 * Il riquadro che risponde a «è tutto a posto?».
 *
 * Gli avvisi arrivano dal server già decisi e già ordinati per gravità: la
 * regola su cosa sia un problema sta nel backend, che la conosce, non qui.
 * Questo componente li disegna e basta — aggiungere un controllo domani non
 * richiederà di toccarlo.
 */
function Cruscotto({ dati }) {
  if (!dati) return null;
  const { alta = 0, media = 0, bassa = 0 } = dati.conteggio_gravita || {};
  const tuttoOk = alta + media + bassa === 0;

  return (
    <div className="mb-8" data-testid="cruscotto-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="font-display font-extrabold text-slate-900 flex items-center gap-2">
          {tuttoOk
            ? <ShieldCheck className="h-5 w-5 text-emerald-500" />
            : <ShieldAlert className="h-5 w-5 text-amber-500" />}
          Stato dell'oratorio
        </p>
        {!tuttoOk && (
          <div className="flex gap-2 text-[11px] font-bold">
            {alta > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">{alta} da sistemare</span>}
            {media > 0 && <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800">{media} da vedere</span>}
            {bassa > 0 && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">{bassa} promemoria</span>}
          </div>
        )}
      </div>

      {tuttoOk ? (
        <div className="card-app p-6 text-center" data-testid="cruscotto-ok">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400 mb-2" strokeWidth={1.5} />
          <p className="font-display font-bold text-slate-700">Nessun adempimento in sospeso</p>
          <p className="text-sm text-slate-400 font-semibold">
            Informative pubblicate, permessi assegnati, nessuna richiesta in scadenza.
          </p>
        </div>
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-2">
          {dati.avvisi.map((a) => {
            const st = STILE_GRAVITA[a.gravita] || STILE_GRAVITA.bassa;
            return (
              <motion.div key={a.chiave} variants={item}
                className={`rounded-2xl border p-4 flex items-start gap-3 ${st.box}`}
                data-testid={`avviso-${a.chiave}`}>
                <span className={`h-2 w-2 rounded-full mt-2 shrink-0 ${st.punto}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-bold ${st.testo}`}>{a.titolo}</p>
                  <p className="text-xs text-slate-600 font-semibold leading-relaxed mt-0.5">
                    {a.dettaglio}
                  </p>
                </div>
                {a.azione && (
                  <Link to={a.azione.link}
                    className="text-xs font-bold text-sky-700 shrink-0 flex items-center gap-1 mt-0.5"
                    data-testid={`avviso-azione-${a.chiave}`}>
                    {a.azione.testo} <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Conservazione dati={dati.conservazione} />
        <Sicurezza dati={dati.sicurezza} />
      </div>
    </div>
  );
}

function Riga({ etichetta, valore, nota }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs font-semibold text-slate-500 shrink-0">{etichetta}</span>
      <span className="text-sm font-bold text-slate-800 text-right min-w-0">
        {valore}
        {nota && <span className="block text-[11px] font-semibold text-slate-400">{nota}</span>}
      </span>
    </div>
  );
}

function Conservazione({ dati }) {
  if (!dati) return null;
  const m = dati.ultima_manutenzione;
  return (
    <div className="card-app p-5" data-testid="cruscotto-conservazione">
      <p className="font-display font-extrabold text-slate-900 flex items-center gap-2 mb-3">
        <Archive className="h-5 w-5 text-amber-500" /> Conservazione
      </p>
      <Riga etichetta="Stagione in corso" valore={dati.stagione_corrente} />
      <Riga etichetta="Annate nei dati"
        valore={(dati.annate || []).map((a) => a.stagione).join(" · ") || "—"}
        nota={(dati.annate || []).some((a) => a.stato === "stimata")
          ? "alcune con data di chiusura stimata" : null} />
      <Riga etichetta="Ultima manutenzione"
        valore={m ? fmtQuando(m.eseguito_il) : "mai eseguita"}
        nota={m?.simulazione ? "era una simulazione" : null} />
      <Riga etichetta="Prossima cancellazione"
        valore={dati.prossima_cancellazione
          ? fmtQuando(dati.prossima_cancellazione.quando) : "nessuna programmata"}
        nota={dati.prossima_cancellazione?.collezione} />
      <Riga etichetta="File orfani sul server" valore={dati.file?.orfani ?? 0} />
      <Riga etichetta="Consensi in archivio" valore={dati.archivio_consensi ?? 0}
        nota="conservati 5 anni come prova" />
    </div>
  );
}

const ETICHETTE_EVENTI = {
  login_fallito: "Accessi falliti",
  login_riuscito: "Accessi riusciti",
  blocco_account: "Account bloccati",
  rate_limit: "Limiti superati",
  attivazione_riuscita: "Primi accessi completati",
  attivazione_fallita: "Primi accessi falliti",
  codice_invalidato: "Codici invalidati",
  reset_accesso: "Accessi reimpostati",
  codice_generato: "Codici generati",
  chiusura_sessione: "Sessioni chiuse",
};

function Sicurezza({ dati }) {
  if (!dati) return null;
  const eventi = Object.entries(dati.eventi || {});
  return (
    <div className="card-app p-5" data-testid="cruscotto-sicurezza">
      <p className="font-display font-extrabold text-slate-900 flex items-center gap-2 mb-1">
        <Activity className="h-5 w-5 text-sky-500" /> Sicurezza
      </p>
      {/* Conteggi, mai nomi: una classifica di chi sbaglia la password sarebbe
          esattamente la sorveglianza che il registro è stato riscritto per
          evitare. Chi ha bisogno del dettaglio apre il registro, dove la
          lettura viene tracciata. */}
      <p className="text-[11px] font-semibold text-slate-400 mb-2">
        Ultimi {dati.giorni} giorni · solo conteggi, nessun nome
      </p>
      {eventi.length === 0 ? (
        <p className="text-sm text-slate-400 font-semibold py-2">Nessun evento registrato.</p>
      ) : (
        eventi.map(([k, v]) => (
          <Riga key={k} etichetta={ETICHETTE_EVENTI[k] || k.replace(/_/g, " ")} valore={v} />
        ))
      )}
      <Riga etichetta="Sessioni aperte" valore={dati.sessioni_attive} />
      <Riga etichetta="Amministratori" valore={dati.amministratori}
        nota={dati.amministratori_nominativi > 0
          ? `${dati.amministratori_nominativi} nominativi` : "solo l'account di sistema"} />
      <Riga etichetta="Detentore account di sistema" valore={dati.detentore || "non dichiarato"} />
      <Link to="/registro" className="text-xs font-bold text-sky-600 flex items-center gap-1 mt-3">
        Registro completo <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}


/* ==========================================================================
   COSE DA FARE — il cuore della pagina del Responsabile
   ========================================================================== */

/**
 * Prima questa pagina descriveva soltanto: quanti iscritti, quante squadre.
 * Numeri veri e inerti, perché non suggerivano niente da fare — si guardano
 * una volta e poi si smette di aprirla.
 *
 * Qui compare solo ciò su cui si può agire, e solo quando c'è. Se non c'è
 * niente il riquadro lo dice: anche «non manca nulla» è un'informazione, ed è
 * quella che permette di chiudere la pagina tranquilli.
 */
function DaFare({ dati }) {
  const d = dati.da_fare || {};
  const voci = [];

  if (d.richieste_in_attesa > 0) voci.push({
    chiave: "richieste", urgente: true,
    testo: `${d.richieste_in_attesa} iscrizioni da approvare`,
    nota: "Arrivate dal modulo pubblico", link: "/segreteria", azione: "Segreteria",
  });
  if (d.minorenni_bloccati > 0) voci.push({
    chiave: "minorenni", urgente: true,
    testo: `${d.minorenni_bloccati} operatori minorenni non possono accedere`,
    nota: "Manca l'autorizzazione firmata da un genitore",
    link: "/animatori", azione: "Registra",
  });
  if (dati.programmi_oggi?.giorno_attivo && dati.programmi_oggi.mancanti?.length > 0) voci.push({
    chiave: "programma",
    testo: `${dati.programmi_oggi.mancanti.length} gruppi senza programma di oggi`,
    nota: dati.programmi_oggi.mancanti.slice(0, 4).join(", "),
    link: "/programma", azione: "Programma",
  });
  if (d.squadre_senza_presenze?.length > 0) voci.push({
    chiave: "presenze",
    testo: `${d.squadre_senza_presenze.length} squadre senza presenze registrate oggi`,
    nota: d.squadre_senza_presenze.slice(0, 4).join(", "),
    link: "/presenze", azione: "Presenze",
  });
  if (d.senza_squadra > 0) voci.push({
    chiave: "senza_squadra",
    testo: `${d.senza_squadra} iscritti senza squadra`,
    nota: "Non compaiono in classifica né nelle presenze",
    link: "/squadre", azione: "Squadre",
  });
  if (d.quota_non_pagata > 0) voci.push({
    chiave: "quote",
    testo: `${d.quota_non_pagata} quote non ancora versate`,
    nota: null, link: "/animati", azione: "Animati",
  });

  if (voci.length === 0) {
    return (
      <div className="card-app p-5 flex items-center gap-3 mb-6" data-testid="dafare-vuoto">
        <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" strokeWidth={1.5} />
        <div>
          <p className="font-bold text-slate-700">Non c'è niente in sospeso</p>
          <p className="text-xs text-slate-400 font-semibold">
            Iscrizioni gestite, programma scritto, presenze registrate.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div variants={stagger} initial="hidden" animate="show"
      className="space-y-2 mb-6" data-testid="dafare">
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Da fare</p>
      {voci.map((v) => (
        <motion.div key={v.chiave} variants={item}
          className={`rounded-2xl border p-4 flex items-center gap-3 ${
            v.urgente ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}
          data-testid={`dafare-${v.chiave}`}>
          <Clock className={`h-4 w-4 shrink-0 ${v.urgente ? "text-amber-600" : "text-slate-400"}`} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-800">{v.testo}</p>
            {v.nota && <p className="text-xs text-slate-500 font-semibold truncate">{v.nota}</p>}
          </div>
          <Link to={v.link} className="text-xs font-bold text-sky-700 shrink-0 flex items-center gap-1"
            data-testid={`dafare-azione-${v.chiave}`}>
            {v.azione} <ArrowRight className="h-3 w-3" />
          </Link>
        </motion.div>
      ))}
    </motion.div>
  );
}


/* ==========================================================================
   QUADRO OPERATIVO
   ========================================================================== */

function Operativo({ dati, classifica }) {
  const stats = [
    { label: "Animati iscritti", value: `${dati.tot_animati}/${dati.max_animati}`, icon: Users, color: "bg-amber-100 text-amber-600", to: "/animati" },
    { label: "Richieste in attesa", value: dati.richieste_in_attesa, icon: Inbox, color: "bg-sky-100 text-sky-600", to: "/segreteria" },
    { label: "Quote non pagate", value: dati.quota_non_pagata, icon: AlertCircle, color: dati.quota_non_pagata > 0 ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600", to: "/animati" },
    { label: "Squadre attive", value: dati.tot_squadre, icon: Flag, color: "bg-emerald-100 text-emerald-600", to: "/squadre" },
  ];

  return (
    <>
      <motion.div variants={stagger} initial="hidden" animate="show"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => (
          <motion.div key={s.label} variants={item}>
            <Link to={s.to} className="card-app p-5 flex flex-col gap-3 hover:-translate-y-1 hover:shadow-lg transition-all block"
              data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${s.color}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{s.label}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {dati.programmi_oggi?.giorno_attivo && dati.programmi_oggi.tot_gruppi > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <Link to="/programma" className="card-app p-5 flex items-center gap-4 hover:-translate-y-1 hover:shadow-lg transition-all block"
            data-testid="dashboard-programmi">
            <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${dati.programmi_oggi.mancanti.length > 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}>
              <ClipboardList className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-slate-800 text-sm">
                Programma di oggi · {dati.programmi_oggi.compilati}/{dati.programmi_oggi.tot_gruppi} gruppi
              </p>
              <p className="text-xs text-slate-400 font-semibold truncate">
                {dati.programmi_oggi.mancanti.length === 0
                  ? "Tutti i gruppi hanno pubblicato il programma"
                  : `Manca: ${dati.programmi_oggi.mancanti.slice(0, 3).join(", ")}${dati.programmi_oggi.mancanti.length > 3 ? ` e altri ${dati.programmi_oggi.mancanti.length - 3}` : ""}`}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
          </Link>
        </motion.div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="card-app p-6" data-testid="dashboard-classifica">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" /> Podio
            </h2>
            <Link to="/classifica" className="text-xs font-bold text-sky-600 flex items-center gap-1">
              Classifica completa <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {classifica.length === 0 ? (
            <p className="text-sm text-slate-400 font-semibold">Nessuna squadra ancora. Crea le squadre per iniziare!</p>
          ) : (
            classifica.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
                <span className={`font-display font-extrabold text-lg w-7 text-center ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : "text-orange-400"}`}>{s.posizione}°</span>
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: s.colore || "#94a3b8" }} />
                <span className="font-bold text-slate-800 flex-1 truncate">{s.nome}</span>
                <span className="font-display font-extrabold text-slate-900">{s.punti} pt</span>
              </div>
            ))
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="card-app p-6" data-testid="dashboard-attivita">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-sky-500" /> Prossime attività
            </h2>
            <Link to="/calendario" className="text-xs font-bold text-sky-600 flex items-center gap-1">
              Calendario <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {dati.prossime_attivita.length === 0 ? (
            <p className="text-sm text-slate-400 font-semibold">Nessuna attività in programma.</p>
          ) : (
            dati.prossime_attivita.map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
                <div className="rounded-2xl bg-sky-50 px-3 py-1.5 text-center shrink-0">
                  <p className="text-xs font-extrabold text-sky-700">{fmtData(a.data).slice(0, 5)}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{a.titolo}</p>
                  <p className="text-xs text-slate-400 font-semibold">
                    {a.ora_inizio} – {a.ora_fine}{!a.visibile_animatori && " · riservata"}
                  </p>
                </div>
              </div>
            ))
          )}
        </motion.div>
      </div>
    </>
  );
}
